import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import * as React from 'react'
import { useEffect } from 'react'
import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import { Custom500 } from '@/pages/500'
import { CheckoutError } from '@/scopes/checkout/components/CheckoutError/CheckoutError'
import { CheckoutHeader } from '@/scopes/checkout/components/CheckoutHeader'
import { CheckoutPreviousStepButton } from '@/scopes/checkout/components/CheckoutPreviousStepHeader/CheckoutPreviousStepHeader'
import { Loader, MainContent, PageLayout } from '@/scopes/checkout/components/Layout'
import { ContentCentered } from '@/scopes/checkout/components/Layout/ContentCentered'
import { RyanairTermsAndConditions } from '@/scopes/checkout/components/TermsAndConditions/RyanairTermsAndConditions'
import { createRyanairEvent } from '@/scopes/checkout/domain/analytics'
import { useSendCheckoutEvent } from '@/scopes/checkout/domain/analytics/useSendCheckoutEvent'
import { CheckoutReactContext } from '@/scopes/checkout/domain/CheckoutContext'
import { CheckoutOptimizelyFlagsProvider } from '@/scopes/checkout/domain/CheckoutOptimizelyFlagsProvider'
import { buildUrlWithCurrentParams } from '@/scopes/checkout/domain/helpers/BuildUrlWithCurrentParams'
import { useBookingDraftQuery, useCheckoutSid, useInvalidateRyanairSessionData } from '@/scopes/checkout/domain/hooks'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { createNewRelicLogger } from '@/scopes/common/infrastructure/logging/newRelicLogger'
import { getMandatoryPaymentAbTestUdsUrl, getMandatoryPaymentAbTestUrl } from '../domain/policies/mandatoryPaymentAbTest'
import { getRyanairPreviousStepUrl } from '../domain/policies/publicationCode'
import { RYANAIR_EVENT, RYANAIR_STATE, RYANAIR_TELEMETRY_TYPE } from './constants'

const queryClient = new QueryClient()

function Page() {
  const { isLoading, data } = useBookingDraftQuery()
  const sid = useCheckoutSid()
  const logger = React.useMemo(() => createNewRelicLogger({ scope: 'checkout' }), [])
  const [isSheetOpen, setIsSheetOpen] = React.useState(false)
  const { invalidateSession } = useInvalidateRyanairSessionData()
  useSendCheckoutEvent('checkout_ryanair_reached')
  const { sendEvent } = useAnalytics()
  const { showMandatoryPaymentPage, showMandatoryPaymentPageUds } = useOptimizelyFlagContext()
  const publicationCode = useConfiguration('publicationCode')
  const previousStepUrl = getRyanairPreviousStepUrl(publicationCode)
  const searchParams = useSearchParams()
  const intl = useIntl()

  const handleOpenChange = (isOpen: boolean) => {
    setIsSheetOpen(isOpen)

    sendEvent(
      createRyanairEvent({
        result: isOpen ? 'show ryanair terms and conditions' : 'close ryanair terms and conditions',
        type: 'iframe',
        description: isOpen ? 'confirm button clicked' : 'ryanair terms and conditions closed',
        element: 'button',
      }),
    )
  }

  useEffect(() => {
    if (!data || !sid) {
      return
    }

    logger.setContext('draftId', sid)
    let sessionTTLTimeout: number | null = null

    const handler = (msg: MessageEvent) => {
      if (msg.data.type === RYANAIR_TELEMETRY_TYPE) {
        const payload = msg.data.payload
        logger.logEvent(RYANAIR_EVENT, { ...payload })

        if (payload.state === RYANAIR_STATE.LOADED) {
          if (payload.sessionTTL) {
            if (sessionTTLTimeout !== null) {
              window.clearTimeout(sessionTTLTimeout)
            }
            sessionTTLTimeout = window.setTimeout(() => {
              logger.logEvent(RYANAIR_EVENT, { state: RYANAIR_STATE.CUSTOM_TTL_EXPIRED })
            }, payload.sessionTTL * 1000)
          }
        }
        else if (payload.state === RYANAIR_STATE.REDIRECT && payload.status === 200) {
          window.clearTimeout(sessionTTLTimeout ?? undefined)

          let url = data?.navigation?.nextStep?.url
          if (showMandatoryPaymentPageUds) {
            url = getMandatoryPaymentAbTestUdsUrl(url)
          }
          if (showMandatoryPaymentPage) {
            url = getMandatoryPaymentAbTestUrl(url)
          }
          if (url) {
            window.location.href = buildUrlWithCurrentParams(url)
          }
        }
        else if (payload.state === RYANAIR_STATE.REDIRECT && payload.status === 440) {
          window.clearTimeout(sessionTTLTimeout ?? undefined)
          handleOpenChange(false)
          invalidateSession()
        }
        else if (payload.state === RYANAIR_STATE.SESSION_ERROR || payload.state === RYANAIR_STATE.TIMEOUT) {
          window.clearTimeout(sessionTTLTimeout ?? undefined)
          handleOpenChange(false)
          invalidateSession()
        }
      }
    }

    window.addEventListener('message', handler, false)
    return () => {
      window.removeEventListener('message', handler, false)
      if (sessionTTLTimeout) {
        window.clearTimeout(sessionTTLTimeout)
      }
    }
  }, [data, sid])

  return (
    <PageLayout>
      {data?.error && data.error.hasError && <CheckoutError />}
      {(!data?.error || (data?.error && !data.error.hasError)) && (
        <NewRelicErrorBoundary scope="checkout-page" fallback={<Custom500 brand="sunweb" />}>
          <CheckoutHeader showBackButton={false}>
            {previousStepUrl && (
              <CheckoutPreviousStepButton
                url={buildUrlWithCurrentParams(previousStepUrl, `?${searchParams?.toString() ?? ''}`)}
                text={intl.formatMessage({ id: 'checkout_steps_ryanair_previous_step' })}
              />
            )}
          </CheckoutHeader>
          <MainContent>
            {isLoading
              ? (
                  <Loader />
                )
              : (
                  <ContentCentered>
                    <RyanairTermsAndConditions isOpen={isSheetOpen} onOpenChange={handleOpenChange} />
                  </ContentCentered>
                )}
          </MainContent>
        </NewRelicErrorBoundary>
      )}
    </PageLayout>
  )
}

export default function RyanairConfirm() {
  const contextItemId = useCMSPageField('contextItemId')
  const checkoutContextValue = React.useMemo(() => ({
    contextItemId,
    step: 4,
  }), [contextItemId])

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutReactContext value={checkoutContextValue}>
        <CheckoutOptimizelyFlagsProvider>
          <MetaTitle titleTranslationTag="checkout_steps_ryanair_step_meta_title" />
          <Page />
          <DevTools />
        </CheckoutOptimizelyFlagsProvider>
      </CheckoutReactContext>
    </QueryClientProvider>
  )
}
